import { useState } from "react";
import QRCode from "react-qr-code";
import { Bell, Download, Smartphone, Star } from "lucide-react";
import ScrollReveal from "./ScrollReveal";
import SuccessCheckmark from "./SuccessCheckmark";
import { trackEvent } from "../utils/analytics";
import { APP_RATINGS, type StoreRating } from "../data/appRatings";

// TODO(backend): replace with the real published listing URLs once the
// ZenFresh app is live on each store.
const PLAY_STORE_URL = "https://play.google.com/store/apps";
const APP_STORE_URL = "https://apps.apple.com";

// TODO(backend): derive this from real store-availability/geo data (e.g. a
// region lookup against published store markets) instead of a static flag.
const APP_AVAILABLE_IN_REGION = true;

interface StoreBadgeProps {
  store: "play" | "apple";
  href: string;
  rating?: StoreRating;
}

function StoreBadge({ store, href, rating }: StoreBadgeProps) {
  const [showQr, setShowQr] = useState(false);
  const label = store === "play" ? "Google Play" : "App Store";

  return (
    <div
      className="relative"
      onMouseEnter={() => setShowQr(true)}
      onMouseLeave={() => setShowQr(false)}
    >
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        onClick={() => trackEvent("app_download_click", { store })}
        onFocus={() => setShowQr(true)}
        onBlur={() => setShowQr(false)}
        className="flex items-center gap-3 rounded-2xl bg-white/10 px-5 py-3 ring-1 ring-white/15 transition-colors hover:bg-white/15"
      >
        <Smartphone className="h-6 w-6 text-teal-300" strokeWidth={1.5} />
        <span className="text-left">
          <span className="block text-[10px] uppercase tracking-wide text-foam-100/60">
            {store === "play" ? "Get it on" : "Download on the"}
          </span>
          <span className="block font-display text-sm font-semibold text-foam-50">{label}</span>
        </span>
      </a>

      {/* Only renders once APP_RATINGS has real data for this store — no
          fabricated star rating in the meantime (see ZP-97). */}
      {rating && (
        <a
          href={rating.reviewsUrl}
          target="_blank"
          rel="noreferrer"
          onClick={() => trackEvent("app_rating_click", { store })}
          className="mt-1.5 flex items-center justify-center gap-1 text-xs text-foam-100/60 transition-colors hover:text-foam-50"
        >
          <Star className="h-3 w-3 fill-current text-teal-300" />
          {rating.rating.toFixed(1)} · {rating.reviewCount.toLocaleString()} reviews
        </a>
      )}

      {showQr && (
        <div className="absolute bottom-full left-1/2 z-10 mb-3 hidden -translate-x-1/2 flex-col items-center rounded-xl bg-white p-3 shadow-lg sm:flex">
          <QRCode value={href} size={104} bgColor="#ffffff" fgColor="#0b1030" />
          <span className="mt-2 text-[11px] font-medium text-ocean-800">Scan to install</span>
        </div>
      )}
    </div>
  );
}

function NotifyMeForm() {
  const [submitted, setSubmitted] = useState(false);

  if (submitted) {
    return (
      <div className="flex items-center gap-2 rounded-full bg-white/10 px-5 py-3 text-sm font-medium text-foam-50 ring-1 ring-white/15">
        <SuccessCheckmark className="h-4 w-4 text-teal-300" />
        We&apos;ll email you when the app launches in your region.
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        trackEvent("app_notify_me_submit");
        setSubmitted(true);
      }}
      className="flex w-full max-w-sm overflow-hidden rounded-full bg-white/10 ring-1 ring-white/15"
    >
      <input
        type="email"
        required
        aria-label="Email address"
        placeholder="you@email.com"
        className="w-full bg-transparent px-4 py-3 text-sm text-foam-50 placeholder:text-foam-100/40 focus:outline-none"
      />
      <button
        type="submit"
        className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap bg-teal-400 px-4 text-sm font-semibold text-ocean-950 transition-colors hover:bg-teal-300"
      >
        <Bell className="h-3.5 w-3.5" />
        Notify Me
      </button>
    </form>
  );
}

export default function AppDownloadSection() {
  return (
    <section id="app-download" className="scroll-mt-24 bg-ocean-900 py-24 text-center text-foam-50">
      <div className="mx-auto max-w-2xl px-6">
        <ScrollReveal>
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-400/15 text-teal-300">
            <Download className="h-6 w-6" strokeWidth={1.5} />
          </div>
          <h2 className="mt-4 font-display text-3xl font-bold sm:text-4xl">Get the ZenFresh App</h2>
          <p className="mt-3 text-foam-100/70">
            Faster reordering, live delivery tracking, and app-only catch alerts — order from your
            phone in a couple of taps.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            {APP_AVAILABLE_IN_REGION ? (
              <>
                <StoreBadge
                  store="play"
                  href={PLAY_STORE_URL}
                  rating={APP_RATINGS?.find((r) => r.store === "play")}
                />
                <StoreBadge
                  store="apple"
                  href={APP_STORE_URL}
                  rating={APP_RATINGS?.find((r) => r.store === "apple")}
                />
              </>
            ) : (
              <NotifyMeForm />
            )}
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
